import React from 'react';
import styled from 'styled-components';
import { WidgetFieldWrapper } from '../contacts/ContactOverview/DND/dndTypes';
import StyledText from '../../components/StyledComponents/StyledText';
import { useTheme } from '../../middleware/ThemeContext';
import Badge from '../../components/Badge';
import { CorporateTheme, Theme } from '../../utils/themes';

interface AddressGridProps {
    addresses: WidgetFieldWrapper[];
    width: number; // 8 ya 12
    primaryAddressType?: string;
}

const AddressGrid: React.FC<AddressGridProps> = ({ addresses, width, primaryAddressType }) => {

    const { theme } = useTheme();
    const columns = width === 8 ? 3 : width === 12 ? 4 : width === 4 ? 2 : 3; // default to 3

    const getPrimaryFieldName = (type: string): string => {
        switch (type) {
            case 'Work': return 'Work Address';
            case 'Home': return 'Home Address';
            case 'Other': return 'Other Address';
            default: return '';
        }
    };

    const visibleAddresses = addresses?.filter(
        addr => addr?.field?.accessKey !== 'contact_primary_address_type'
    );
    const groupedAddresses = visibleAddresses?.reduce((acc, addr) => {
        const sectionName = addr?.sectionName;
        if (!sectionName) return acc;

        if (!acc[sectionName]) {
            acc[sectionName] = [];
        }
        acc[sectionName].push(addr);
        return acc;
    }, {} as Record<string, any[]>);

    const formatAddress = (fields: any[]): string => {
        const fieldMap: Record<string, string> = {};

        fields?.forEach(field => {
            const name = (field?.field?.name || '').toLowerCase();
            const value = (field?.field?.value || '').toString().trim();
            if (!value) return;
            if (name?.includes('directory')) return;

            if (name.includes('line 1')) fieldMap.line1 = value;
            else if (name.includes('line 2')) fieldMap.line2 = value;
            else if (name.includes('city')) fieldMap.city = value;
            else if (name.includes('state')) fieldMap.state = value.toUpperCase();
            else if (name.includes('zip') || name.includes('postal')) fieldMap.zip = value;
            else if (name.includes('country')) fieldMap.country = value;
        });

        const lines: string[] = [];

        if (fieldMap?.line1) lines.push(fieldMap?.line1);
        if (fieldMap?.line2) lines.push(fieldMap?.line2);

        const city = fieldMap?.city || '';
        const state = fieldMap?.state || '';
        const zip = fieldMap?.zip || '';

        if (city || state || zip) {
            let cityStateZip = '';
            if (city) cityStateZip += city;
            if (state || zip) {
                if (city) cityStateZip += ', ';
                if (state) cityStateZip += state;
                if (zip) {
                    if (state) cityStateZip += ' ';
                    cityStateZip += zip;
                }
            }
            lines.push(cityStateZip);
        }

        if (fieldMap.country) lines?.push(fieldMap?.country);

        return lines.join('\t');
    };

    return (
        <ScrollContainer theme={theme}>
            {Object.entries(groupedAddresses).map(([sectionName, fields], index) => {
                const isPrimary = primaryAddressType &&
                    sectionName === getPrimaryFieldName(primaryAddressType);

                const fullAddress = formatAddress(fields);

                return (
                    <AddressCard key={index} theme={theme}>
                        <Header>
                            <StyledText fontSize="14px" fontWeight={600} textColor={theme.CorporateTheme.colors.textSecondary}>
                                {sectionName}
                            </StyledText>
                            {isPrimary && (
                                <Badge
                                    bgColor={CorporateTheme.colors.green}
                                    text="Primary"
                                    color={theme.CorporateTheme.colors.white}
                                    fontWeight={600}
                                    fontSize='12px'
                                    borderRadius='38px'
                                />
                            )}
                        </Header>
                        <StyledText
                            fontSize="14px"
                            fontWeight={400}
                            textColor={theme.CorporateTheme.colors.textSecondary}
                            className='mt-1'
                            lineHeight='1.5'
                            style={{ whiteSpace: 'pre-line' }} // 👈 important for line breaks
                        >
                            {fullAddress}
                        </StyledText>
                    </AddressCard>
                );
            })}
        </ScrollContainer>
    );
};

const ScrollContainer = styled.div<{ theme: Theme }>`
  display: flex;
  flex-wrap: nowrap;   

  gap: 20px;
  padding: 8px 0;
  -webkit-overflow-scrolling: touch;
 background: ${({ theme }) => theme.CorporateTheme.colors.background};

`;

const AddressCard = styled.div<{ theme: Theme }>`
  flex: 0 0 280px;            /* 👈 0 grow, 0 shrink, fixed basis width */
  border-radius: 8px;
  padding: 16px 5px;
  background: ${({ theme }) => theme.CorporateTheme.colors.background};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;


export default AddressGrid;